<template>
  <div class="h-full w-full flex flex-col">
    <div v-if="isDemo" class="demo-banner" role="status">
      DEMONSTRATION PURPOSES ONLY - THIS PAGE IS NOT CONNECTED TO A BACK-END SERVER
    </div>
    <Navbar :user="user"/>
    <div id="page">
      <router-view
        @set-user="user = $event"
        :user="user"
      />
    </div>
  </div>
</template>

<script setup>
// import { ref, watch, provide } from 'vue'

import Navbar from './components/Navbar.vue'
import useAuthentication from "./composables/useAuthentication"
import useCurrentUser from "./composables/useCurrentUser"

const isDemo = __DEMO_MODE__
const { authenticated } = useAuthentication()
const { user } = useCurrentUser(authenticated)

</script>

<style>
.demo-banner {
  flex-shrink: 0;
  background: #1e3a5f;
  color: #f5f7fa;
  text-align: center;
  font-size: 0.875rem;
  font-weight: 500;
  letter-spacing: 0.01em;
  padding: 0.45rem 1rem;
  z-index: 1100;
}

html, body {
  padding: 0;
  margin: 0;
}

#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  /* text-align: center; */
  color: #2c3e50;
  min-width: 100vw;
  min-height: 100vh;
  height: 100vh;
  width: 100vw;
  position: relative;
}

#page {
  padding: 1em;
  flex-grow: 1;
  max-height: 100%;
  /* overflow: hidden; */
}

.card {
  border-radius: 0.25rem;
  box-shadow: rgba(0, 0, 0, 0) 0px 0px 0px 0px,
  rgba(0, 0, 0, 0) 0px 0px 0px 0px,
  rgba(0, 0, 0, 0.1) 0px 1px 3px 0px,
  rgba(0, 0, 0, 0.06) 0px 1px 2px 0px
}

.selectable {
  border-radius: 0.125rem;
}

.selectable:hover {
  background-color: rgb(244, 244, 244);
  cursor: pointer;
}

.selectable:hover > * {
  color: maroon;
}

.underline {
  box-sizing: border-box;
  border-bottom: 1px rgba(119, 0, 0, 0.621) solid;
}

.basic-border {
  box-sizing: border-box;
  border: 1px rgba(61, 62, 63, 0.513) solid;
}

.uppercase {
  text-transform: uppercase;
}

.lowercase {
  text-transform: lowercase;
}

.normal-case {
  text-transform: none;
}

/*========================= Height presets =========================*/
.h-px {
  height: 1px;
}

.h-0\.5 {
  height: 0.125rem;
}

.h-1 {
  height: 0.25rem;
}

.h-1\.5 {
  height: 0.375rem;
}

.h-2 {
  height: 0.5rem;
}

.h-2\.5 {
  height: 0.625rem;
}

.h-3 {
  height: 0.75rem;
}

.h-3\.5 {
  height: 0.875rem;
}

.h-4 {
  height: 1rem;
}

.h-5 {
  height: 1.25rem;
}

.h-6 {
  height: 1.5rem;
}

.h-7 {
  height: 1.75rem;
}

.h-8 {
  height: 2rem;
}

.h-9 {
  height: 2.25rem;
}

.h-10 {
  height: 2.5rem;
}

.h-11 {
  height: 2.75rem;
}

.h-12 {
  height: 3rem;
}

.h-14 {
  height: 3.5rem;
}

.h-16 {
  height: 4rem;
}

.h-20 {
  height: 5rem;
}

.h-24 {
  height: 6rem;
}

.h-28 {
  height: 7rem;
}

.h-32 {
  height: 8rem;
}

.h-36 {
  height: 9rem;
}

.h-40 {
  height: 10rem;
}

.h-44 {
  height: 11rem;
}

.h-48 {
  height: 12rem;
}

.h-half {
  height: 50%;
}

.h-full {
  height: 100%;
}

.h-screen {
  height: 100vh;
}

.max-h-full {
  max-height: 100%;
}

/*============================ Width presets ============================*/
.w-1\/2 {
  width: 50%;
}

.w-px {
  width: 1px;
}

.w-0\.5 {
  width: 0.125rem;
}

.w-1 {
  width: 0.25rem;
}

.w-1\.5 {
  width: 0.375rem;
}

.w-2 {
  width: 0.5rem;
}

.w-2\.5 {
  width: 0.625rem;
}

.w-3 {
  width: 0.75rem;
}

.w-3\.5 {
  width: 0.875rem;
}

.w-4 {
  width: 1rem;
}

.w-5 {
  width: 1.25rem;
}

.w-6 {
  width: 1.5rem;
}

.w-7 {
  width: 1.75rem;
}

.w-8 {
  width: 2rem;
}

.w-9 {
  width: 2.25rem;
}

.w-10 {
  width: 2.5rem;
}

.w-11 {
  width: 2.75rem;
}

.w-12 {
  width: 3rem;
}

.w-14 {
  width: 3.5rem;
}

.w-16 {
  width: 4rem;
}

.w-20 {
  width: 5rem;
}

.w-24 {
  width: 6rem;
}

.w-28 {
  width: 7rem;
}

.w-32 {
  width: 8rem;
}

.w-36 {
  width: 9rem;
}

.w-40 {
  width: 10rem;
}

.w-44 {
  width: 11rem;
}

.w-48 {
  width: 12rem;
}

.w-52 {
  width: 13rem;
}

.w-56 {
  width: 14rem;
}

.w-60 {
  width: 15rem;
}

.w-64 {
  width: 16rem;
}

.w-68 {
  width: 17rem;
}

.w-72 {
  width: 18rem;
}

.w-76 {
  width: 19rem;
}

.w-80 {
  width: 20rem;
}

.w-84 {
  width: 21rem;
}

.w-88 {
  width: 22rem;
}

.w-96 {
  width: 24rem;
}

.w-112 {
  width: 26rem;
}

.w-120 {
  width: 28rem;
}

.w-124 {
  width: 29rem;
}

.w-128 {
  width: 30rem;
}

.w-half {
  width: 50%;
}

.w-most {
  width: 65%;
}

.w-full {
  width: 100%;
}

.min-w-5 {
 min-width: 1.25rem;
}

.min-w-6 {
  min-width: 1.5rem;
}

.min-w-7 {
  min-width: 1.75rem;
}

.min-w-8 {
  min-width: 2rem;
}

.min-w-9 {
  min-width: 2.25rem;
}

.min-w-10 {
  min-width: 2.5rem;
}

.min-w-12 {
  min-width: 3rem;
}

.min-w-14 {
  min-width: 3.5rem;
}

.min-w-16 {
  min-width: 4rem;
}

.min-w-20 {
  min-width: 5rem;
}

.min-w-28 {
  min-width: 7rem;
}

.min-w-36 {
  min-width: 9rem;
}

.min-w-44 {
  min-width: 11rem;
}

.min-w-60 {
  min-width: 15rem;
}

.min-w-76 {
  min-width: 19rem;
}

.max-w-92 {
  max-width: 23rem;
}

.max-w-108 {
  max-width: 27rem;
}

.max-w-108 {
  max-width: 27rem;
}

.max-w-half {
  max-width: 50%
}

.max-w-2t {
  max-width: 67%
}

.max-w-3q {
  max-width: 75%
}

.disabled {
  opacity: .5;
}

.disabled:hover {
  cursor: not-allowed;
}

.clickable:hover {
  cursor: pointer;
  background: initial;
}

.transparent {
  background: transparent;
}

/*================================= Opacity =================================*/
.opacity-50 {
  opacity: .5;
}

.opacity-100 {
  opacity: 1;
}

/*========================== Curves & Rounded edges ==========================*/
.circular {
  border-radius: 50%;
}

.rounded-sm {
  border-radius: 0.125rem;
}

.rounded {
  border-radius: 0.25rem;
}

.rounded-md {
  border-radius: 0.375rem;
}

.rounded-lg {
  border-radius: 0.5rem;
}

.rounded-xl {
  border-radius: 0.75rem;
}

.rounded-2xl {
  border-radius: 1rem;
}

.rounded-3xl {
  border-radius: 1.5rem;
}

.rounded-full {
  border-radius: 9999px;
}

.rounded-top {
  border-top-left-radius: 0.25rem;
  border-top-right-radius: 0.25rem;
}

.instructions-box {
  position: relative;
  border-radius: 0.25rem;
  background: rgba(249, 250, 251);
  box-sizing: border-box;
  border: 1px rgba(61, 62, 63, 0.513) solid;
  box-shadow: rgba(0, 0, 0, 0) 0px 0px 0px 0px,
  rgba(0, 0, 0, 0) 0px 0px 0px 0px,
  rgba(0, 0, 0, 0.1) 0px 1px 3px 0px,
  rgba(0, 0, 0, 0.06) 0px 1px 2px 0px
}

/*================================ Cursor ================================*/
.cursor-pointer {
  cursor: pointer;
}

.cursor-not-allowed {
  cursor: not-allowed;
}

/*========================== Display shorthands ==========================*/
.block {
  display: block;
}

.inline-block {
  display: inline-block;
}

.inline {
  display: inline;
}

.flex {
  display: flex;
}

/* Position shorthands */
.relative {
  position: relative;
}

.absolute {
  position: absolute;
}

.fixed {
  position: fixed;
}

/* Flexbox */
.flex-row {
  flex-direction: row;
}

.flex-col {
  flex-direction: column;
}

.justify-between {
  justify-content: space-between;
}

.justify-evenly {
  justify-content: space-between;
}

.justify-center {
  justify-content: center;
}

.justify-end {
  justify-content: flex-end;
}

.justify-start {
  justify-content: flex-start;
}

.items-center {
  align-items: center;
}

.items-baseline {
  align-items: baseline;
}

.flex-grow {
  flex-grow: 1;
}

.flex-grow-0 {
  flex-grow: 0;
}

.flex-shrink {
  flex-shrink: 1;
}

.flex-shrink-0 {
  flex-shrink: 0;
}

.gap-1 {
  gap: 0.25rem; /* 4px */
}

.gap-2 {
  gap: 0.5rem; /* 8px */
}

.gap-4 {
  gap: 1rem; /* 16px */
}

/*============================= Font Weight =============================*/
.weight-500 {
  font-weight: 500;
}

.weight-600 {
  font-weight: 600;
}

.weight-700 {
  font-weight: 700;
}

.weight-900 {
  font-weight: 900;
}

/*============================== Text Size ==============================*/
.text-xs {
  font-size: 0.75rem;
  line-height: 1rem;
}

.text-sm {
  font-size: 0.875rem;
  line-height: 1.25rem;
}

.text-md {
  font-size: 1rem;
  line-height: 1.5rem;
}

.text-lg {
  font-size: 1.125rem;
  line-height: 1.75rem;
}

.text-xl {
  font-size: 1.25rem;
  line-height: 1.75rem;
}

.text-2xl {
  font-size: 1.5rem;
  line-height: 2rem;
}

.text-3xl {
  font-size: 1.875rem;
  line-height: 2.25rem;
}

/*============================= Text Style =============================*/
.italic {
  font-style: italic;
}

/*=========================== Text Alignment ===========================*/
.text-left {
  text-align: left;
}

.text-center {
  text-align: center;
}

.text-right {
  text-align: right;
}

/*============================= Text Color =============================*/
.text-white {
  color: white;
}

.text-primary {
  color: maroon;
}

.text-secondary {
  color: rgb(37, 113, 235);
}

.text-maroon {
  color: maroon;
}

.text-red {
  color: red;
}

.text-green-1 {
  color: rgb(18, 82, 5);
}

.text-gray-1 {
  color: rgba(249, 250, 251);
}

.text-gray-2 {
  color: rgba(243, 244, 246);
}

.text-gray-3 {
  color: rgba(229, 231, 235);
}

.text-gray-4 {
  color: rgba(209, 213, 219);
}

.text-gray-5 {
  color: rgba(156, 163, 175);
}

.text-gray-6 {
  color: rgba(107, 114, 128);
}

.text-gray-7 {
  color: rgba(75, 85, 99);
}

.text-gray-8 {
  color: rgba(55, 65, 81);
}

.text-gray-9 {
  color: rgba(31, 41, 55);
}

.text-gray-10 {
  color: rgba(17, 24, 3);
}

/*================================ Background ================================*/

.bg-white {
  background: white;
}

.bg-gray-1 {
  background: rgba(249, 250, 251);
}

.bg-gray-2 {
  background: rgba(243, 244, 246);
}

.bg-gray-3 {
  background: rgba(229, 231, 235);
}

.bg-gray-4 {
  background: rgba(209, 213, 219);
}

.bg-gray-5 {
  background: rgba(156, 163, 175);
}

.bg-gray- {
  background: rgba(107, 114, 128);
}

.bg-gray-7 {
  background: rgba(75, 85, 99);
}

.bg-gray-8 {
  background: rgba(55, 65, 81);
}

.bg-gray-9 {
  background: rgba(31, 41, 55);
}

.bg-gray-10 {
  background: rgba(17, 24, 3);
}

.bg-green-1 {
  background: rgba(214, 252, 208, 0.696);
}

/*================================ Padding ================================*/

.p-0\.5 {
  padding: 0.125rem;
}

.p-1 {
  padding: 0.25rem;
}

.p-1\.5 {
  padding: 0.375rem;
}

.p-2 {
  padding: 0.5rem;
}

.p-2\.5 {
  padding: 0.625rem;
}

.p-3 {
  padding: 0.75rem;
}

.p-3\.5 {
  padding: 0.875rem;
}

.p-4 {
  padding: 1rem;
}

.p-5 {
  padding: 1.25rem;
}

.p-6 {
  padding: 1.5rem;
}

.p-7 {
  padding: 1.75rem;
}

.p-8 {
  padding: 2rem;
}

.p-9 {
  padding: 2.25rem;
}

.p-10 {
  padding: 2.5rem;
}

.pl-0\.5 {
  padding-left: 0.125rem;
}

.pl-1 {
  padding-left: 0.25rem;
}

.pl-1\.5 {
  padding-left: 0.375rem;
}

.pl-2 {
  padding-left: 0.5rem;
}

.pl-2\.5 {
  padding-left: 0.625rem;
}

.pl-3 {
  padding-left: 0.75rem;
}

.pl-3\.5 {
  padding-left: 0.875rem;
}

.pl-4 {
  padding-left: 1rem;
}

.pl-5 {
  padding-left: 1.25rem;
}

.pl-6 {
  padding-left: 1.5rem;
}

.pl-7 {
  padding-left: 1.75rem;
}

.pl-8 {
  padding-left: 2rem;
}

.pl-9 {
  padding-left: 2.25rem;
}

.pl-10 {
  padding-left: 2.5rem;
}

.pl-14 {
  padding-left: 3.5rem;
}

.pl-18 {
  padding-left: 4.5rem;
}

.pl-24 {
  padding-left: 6rem;
}

.pl-36 {
  padding-left: 9rem;
}

.pr-0\.5 {
  padding-right: 0.125rem;
}

.pr-1 {
  padding-right: 0.25rem;
}

.pr-1\.5 {
  padding-right: 0.375rem;
}

.pr-2 {
  padding-right: 0.5rem;
}

.pr-2\.5 {
  padding-right: 0.625rem;
}

.pr-3 {
  padding-right: 0.75rem;
}

.pr-3\.5 {
  padding-right: 0.875rem;
}

.pr-4 {
  padding-right: 1rem;
}

.pr-5 {
  padding-right: 1.25rem;
}

.pr-6 {
  padding-right: 1.5rem;
}

.pr-7 {
  padding-right: 1.75rem;
}

.pr-8 {
  padding-right: 2rem;
}

.pr-9 {
  padding-right: 2.25rem;
}

.pr-10 {
  padding-right: 2.5rem;
}

.pr-12 {
  padding-right: 3rem;
}

.pr-16 {
  padding-right: 4rem;
}

.pr-18 {
  padding-right: 4.5rem;
}

.pr-20 {
  padding-right: 5rem;
}

.pr-24 {
  padding-right: 6rem;
}

.pb-0\.5 {
  padding-bottom: 0.125rem;
}

.pb-1 {
  padding-bottom: 0.25rem;
}

.pb-1\.5 {
  padding-bottom: 0.375rem;
}

.pb-2 {
  padding-bottom: 0.5rem;
}

.pb-2\.5 {
  padding-bottom: 0.625rem;
}

.pb-3 {
  padding-bottom: 0.75rem;
}

.pb-3\.5 {
  padding-bottom: 0.875rem;
}

.pb-4 {
  padding-bottom: 1rem;
}

.pb-5 {
  padding-bottom: 1.25rem;
}

.pb-6 {
  padding-bottom: 1.5rem;
}

.pb-7 {
  padding-bottom: 1.75rem;
}

.pb-8 {
  padding-bottom: 2rem;
}

.pb-9 {
  padding-bottom: 2.25rem;
}

.pb-10 {
  padding-bottom: 2.5rem;
}

.pb-16 {
  padding-bottom: 4rem;
}

.pt-nudge {
  padding-top: 0.1rem;
}

.pt-1 {
  padding-top: 0.25rem;
}

.pt-1\.5 {
  padding-top: 0.375rem;
}

.pt-2 {
  padding-top: 0.5rem;
}

.pt-2\.5 {
  padding-top: 0.625rem;
}

.pt-3 {
  padding-top: 0.75rem;
}

.pt-3\.5 {
  padding-top: 0.875rem;
}

.pt-4 {
  padding-top: 1rem;
}

.pt-5 {
  padding-top: 1.25rem;
}

.pt-6 {
  padding-top: 1.5rem;
}

.pt-7 {
  padding-top: 1.75rem;
}

.pt-8 {
  padding-top: 2rem;
}

.pt-9 {
  padding-top: 2.25rem;
}

.pt-10 {
  padding-top: 2.5rem;
}

/*================================ Margin ================================*/

.m-auto {
  margin: auto;
}

.m-0 {
  margin: 0;
}

.m-0\.5 {
  margin: 0.125rem;
}

.m-1 {
  margin: 0.25rem;
}

.m-1\.5 {
  margin: 0.375rem;
}

.m-2 {
  margin: 0.5rem;
}

.m-2\.5 {
  margin: 0.625rem;
}

.m-3 {
  margin: 0.75rem;
}

.m-3\.5 {
  margin: 0.875rem;
}

.m-4 {
  margin: 1rem;
}

.m-5 {
  margin: 1.25rem;
}

.m-6 {
  margin: 1.5rem;
}

.m-7 {
  margin: 1.75rem;
}

.m-8 {
  margin: 2rem;
}

.m-9 {
  margin: 2.25rem;
}

.m-10 {
  margin: 2.5rem;
}

.ml-0\.5 {
  margin-left: 0.125rem;
}

.ml-1 {
  margin-left: 0.25rem;
}

.ml-1\.5 {
  margin-left: 0.375rem;
}

.ml-2 {
  margin-left: 0.5rem;
}

.ml-2\.5 {
  margin-left: 0.625rem;
}

.ml-3 {
  margin-left: 0.75rem;
}

.ml-3\.5 {
  margin-left: 0.875rem;
}

.ml-4 {
  margin-left: 1rem;
}

.ml-5 {
  margin-left: 1.25rem;
}

.ml-6 {
  margin-left: 1.5rem;
}

.ml-7 {
  margin-left: 1.75rem;
}

.ml-8 {
  margin-left: 2rem;
}

.ml-9 {
  margin-left: 2.25rem;
}

.ml-10 {
  margin-left: 2.5rem;
}

.ml-16 {
  margin-left: 4rem;
}

.mr-0\.5 {
  margin-right: 0.125rem;
}

.mr-1 {
  margin-right: 0.25rem;
}

.mr-1\.5 {
  margin-right: 0.375rem;
}

.mr-2 {
  margin-right: 0.5rem;
}

.mr-2\.5 {
  margin-right: 0.625rem;
}

.mr-3 {
  margin-right: 0.75rem;
}

.mr-3\.5 {
  margin-right: 0.875rem;
}

.mr-4 {
  margin-right: 1rem;
}

.mr-5 {
  margin-right: 1.25rem;
}

.mr-6 {
  margin-right: 1.5rem;
}

.mr-7 {
  margin-right: 1.75rem;
}

.mr-8 {
  margin-right: 2rem;
}

.mr-9 {
  margin-right: 2.25rem;
}

.mr-10 {
  margin-right: 2.5rem;
}

.mb-0\.5 {
  margin-bottom: 0.125rem;
}

.mb-1 {
  margin-bottom: 0.25rem;
}

.mb-1\.5 {
  margin-bottom: 0.375rem;
}

.mb-2 {
  margin-bottom: 0.5rem;
}

.mb-2\.5 {
  margin-bottom: 0.625rem;
}

.mb-3 {
  margin-bottom: 0.75rem;
}

.mb-3\.5 {
  margin-bottom: 0.875rem;
}

.mb-4 {
  margin-bottom: 1rem;
}

.mb-5 {
  margin-bottom: 1.25rem;
}

.mb-6 {
  margin-bottom: 1.5rem;
}

.mb-7 {
  margin-bottom: 1.75rem;
}

.mb-8 {
  margin-bottom: 2rem;
}

.mb-9 {
  margin-bottom: 2.25rem;
}

.mb-10 {
  margin-bottom: 2.5rem;
}

.mt-0\.5 {
  margin-top: 0.125rem;
}

.mt-1 {
  margin-top: 0.25rem;
}

.mt-1-5 {
  margin-top: 0.375rem;
}

.mt-2 {
  margin-top: 0.5rem;
}

.mt-2\.5 {
  margin-top: 0.625rem;
}

.mt-3 {
  margin-top: 0.75rem;
}

.mt-3\.5 {
  margin-top: 0.875rem;
}

.mt-4 {
  margin-top: 1rem;
}

.mt-5 {
  margin-top: 1.25rem;
}

.mt-6 {
  margin-top: 1.5rem;
}

.mt-7 {
  margin-top: 1.75rem;
}

.mt-8 {
  margin-top: 2rem;
}

.mt-9 {
  margin-top: 2.25rem;
}

.mt-10 {
  margin-top: 2.5rem;
}

/*================================ Overflow Rule ================================*/
.overflow-scroll {
  overflow: scroll;
}

.overflow-hidden {
  overflow: hidden;
}

.overflow-y-scroll {
  overflow-y: scroll;
}

.overflow-y-hidden {
  overflow-y: hidden;
}
.overflow-x-scroll {
  overflow-x: scroll;
}

.overflow-x-hidden {
  overflow-x: hidden;
}

/*================================ Box shadows ================================*/
.shadow {
  box-shadow: rgba(0, 0, 0, 0) 0px 0px 0px 0px,
  rgba(0, 0, 0, 0) 0px 0px 0px 0px,
  rgba(0, 0, 0, 0.1) 0px 1px 3px 0px,
  rgba(0, 0, 0, 0.06) 0px 1px 2px 0px
}

.shadow-xl {
  box-shadow: rgba(0, 0, 0, 0) 0px 0px 0px 0px,
    rgba(0, 0, 0, 0) 0px 0px 0px 0px,
    rgba(0, 0, 0, 0.1) 0px 20px 25px -5px,
    rgba(0, 0, 0, 0.04) 0px 10px 10px -5px;
}

/*============================== Borders ===============================*/

.border-gray {
  border: 1px solid rgb(156, 163, 175); /* uses custom gray*/
}

.border-gray-200 {
  border: 1px solid rgb(243, 244, 246);
}

.border-gray-300 {
  border: 1px solid rgb(229, 231, 235);
}

.border-primary {
  border: 1px solid maroon;
}

.border-secondary {
  border: 1px solid rgb(37, 113, 235);
}

.border-green-1 {
  border: 1.5px solid rgb(8, 139, 17);
}

.img-border {
  border: 1.5px solid black;
}
/* =========================== Text Wrapping ===========================*/

.whitespace-nowrap {
  white-space: nowrap;
}

.whitespace-normal {
  white-space: normal;
}

/*================================ Misc ================================*/
.border-box {
  box-sizing: border-box;
}

.outline-none {
  outline: none;
}

.center-items {
  align-items: center
}

.vertical-middle {
  vertical-align: middle;
}

.self-center {
  align-self: center;
}

.float-right {
  float: right;
}

.invisible {
  visibility: hidden;
}

.transparent-bg {
  mix-blend-mode: multiply;
}

</style>
